if (!customElements.get('sleek-collection-list')) {
  class SleekCollectionList extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.viewport = this.querySelector('[data-sleek-collection-viewport]');
      this.track = this.querySelector('[data-sleek-collection-track]');
      this.cards = Array.from(this.querySelectorAll('[data-sleek-collection-card]'));
      this.section = this.closest('.sleek-collection-list-section');
      this.previousButton = this.section?.querySelector('[data-sleek-collection-previous]');
      this.nextButton = this.section?.querySelector('[data-sleek-collection-next]');
      this.count = this.cards.length;
      this.index = 0;
      this.pointerStart = null;

      if (!this.viewport || !this.track || !this.count) return;

      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.onKeyDown = this.handleKeyDown.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);

      this.previousButton?.addEventListener('click', () => this.previous());
      this.nextButton?.addEventListener('click', () => this.next());
      this.viewport.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.viewport.addEventListener('pointerup', this.onPointerUp, { passive: true });
      this.viewport.addEventListener('pointercancel', this.onPointerUp, { passive: true });
      this.viewport.addEventListener('keydown', this.onKeyDown);

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.viewport);
      requestAnimationFrame(() => this.measure());
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('pointerup', this.onPointerUp);
      this.viewport?.removeEventListener('pointercancel', this.onPointerUp);
      this.viewport?.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    measure() {
      this.isDesktop = window.matchMedia('(min-width: 750px)').matches;
      const desktopColumns = Number(this.dataset.desktopColumns || 4);
      const mobileColumns = Number(this.dataset.mobileColumns || 1);
      this.visibleItems = this.isDesktop ? desktopColumns : mobileColumns;
      this.carouselEnabled = this.isDesktop
        ? this.dataset.enableSlider === 'true' && this.count > desktopColumns
        : this.dataset.swipeMobile === 'true' && this.count > mobileColumns;

      this.maxIndex = Math.max(0, this.count - this.visibleItems);
      this.index = Math.min(this.index, this.maxIndex);
      this.toggleAttribute('data-carousel-active', this.carouselEnabled);
      this.style.setProperty('--sleek-visible-items', String(this.visibleItems));
      this.move(false);
      this.updateControls();
    }

    getStep() {
      const firstCard = this.cards[0];
      if (!firstCard) return 0;
      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap || 0);
      return firstCard.getBoundingClientRect().width + gap;
    }

    move(animate = true) {
      this.track.style.transition = animate ? '' : 'none';
      const offset = this.carouselEnabled ? this.index * this.getStep() : 0;
      this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
      if (!animate) {
        this.track.offsetHeight;
        this.track.style.transition = '';
      }
    }

    next() {
      if (!this.carouselEnabled) return;
      this.index = this.index >= this.maxIndex ? 0 : this.index + 1;
      this.move(true);
      this.updateControls();
    }

    previous() {
      if (!this.carouselEnabled) return;
      this.index = this.index <= 0 ? this.maxIndex : this.index - 1;
      this.move(true);
      this.updateControls();
    }

    goToCard(index, animate = true) {
      if (!this.carouselEnabled) return;
      this.index = Math.max(0, Math.min(Number(index), this.maxIndex));
      this.move(animate);
      this.updateControls();
    }

    updateControls() {
      const showDesktopControls = this.carouselEnabled && this.isDesktop;
      if (this.previousButton) this.previousButton.hidden = !showDesktopControls;
      if (this.nextButton) this.nextButton.hidden = !showDesktopControls;
    }

    handlePointerDown(event) {
      if (!this.carouselEnabled || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.pointerStart = { x: event.clientX, y: event.clientY };
    }

    handlePointerUp(event) {
      if (!this.pointerStart) return;
      const deltaX = event.clientX - this.pointerStart.x;
      const deltaY = event.clientY - this.pointerStart.y;
      this.pointerStart = null;
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      if (deltaX < 0) this.next();
      else this.previous();
    }

    handleKeyDown(event) {
      if (!this.carouselEnabled) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previous();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      }
    }

    handleBlockSelect(event) {
      if (event.detail?.sectionId !== this.dataset.sectionId) return;
      const selected = event.target?.closest('[data-sleek-collection-card]');
      if (!selected) return;
      const index = this.cards.indexOf(selected);
      if (index >= 0) this.goToCard(index, false);
    }
  }

  customElements.define('sleek-collection-list', SleekCollectionList);
}
